import { Link } from 'react-router-dom';
import { Card, CardContent, CardFooter } from '../components/ui/card';
import { Header } from '../components/header';
import { AreaChart } from '../components/ui/chart';
import { monthlyData, performanceData, timeStats } from '../lib/dashboard-data';
import {
  House,
  MapPin,
  ClipboardText,
  Clock,
  ChartLineUp,
  TrendUp,
  CalendarBlank,
  Star,
  StarHalf,
  Timer,
  Trophy,
} from '@phosphor-icons/react';
import { useTranslationWithLanguage } from '../context/language-context';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { useState, useEffect } from 'react';

// Map string icon names to actual components
const iconMap = {
  House,
  MapPin,
  ClipboardText,
  Clock,
  ChartLineUp,
  TrendUp,
};

// Star Rating Component
const StarRating = ({ rating }: { rating: number }) => {
  // Convert rating to number of full and half stars
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  return (
    <div className="flex">
      {[...Array(fullStars)].map((_, i) => (
        <Star key={`full-${i}`} weight="fill" className="w-5 h-5 text-[var(--color-gold-500)]" />
      ))}
      {hasHalfStar && <StarHalf weight="fill" className="w-5 h-5 text-[var(--color-gold-500)]" />}
      {[...Array(emptyStars)].map((_, i) => (
        <Star key={`empty-${i}`} className="w-5 h-5 text-gray-300" />
      ))}
    </div>
  );
};

export default function HomePage() {
  const { t, language } = useTranslationWithLanguage();

  // Points collected today (out of 10)
  const [pointsCollected, setPointsCollected] = useState(7);
  const totalPoints = 10;

  // Time remaining calculation (8 hour workday with 2 hour break)
  const totalWorkHours = 8;
  const [hoursLeft, setHoursLeft] = useState(5); // Starting with 5 hours left
  const [minutesLeft, setMinutesLeft] = useState(30); // and 30 minutes

  // Update countdown timer every minute
  useEffect(() => {
    const timer = setInterval(() => {
      if (minutesLeft > 0) {
        setMinutesLeft(prev => prev - 1);
      } else if (hoursLeft > 0) {
        setHoursLeft(prev => prev - 1);
        setMinutesLeft(59);
      } else {
        clearInterval(timer);
      }
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, [hoursLeft, minutesLeft]);

  return (
    <div
      className="flex flex-col min-h-screen"
      dir={language === 'ar' ? 'rtl' : 'ltr'}
    >
      <Header title={t('home.dashboard')} isRTL={language === 'ar'} />

      <div className="relative top-[5rem] p-3 space-y-3 overflow-y-auto pb-20">
        {/* Profile Card - Styled like the reference image */}
        <div className="rounded-xl overflow-hidden shadow-lg mb-6 relative hidden">
          {/* Gradient background */}
          <div className="bg-gradient-to-r from-red-400 to-blue-500 p-6 pt-12 pb-16 relative">
            {/* Centered avatar */}
            <div className="absolute left-1/2 top-0 transform -translate-x-1/2 -translate-y-1/3 z-10">
              <div className="rounded-full bg-gradient-to-r from-yellow-300 to-yellow-500 p-1.5">
                <Avatar className="h-24 w-24 border-2 border-white">
                  <AvatarImage src="/placeholder-user.jpg" alt="Inspector" />
                  <AvatarFallback className="bg-[var(--color-gold-100)] text-[var(--color-gold-800)] text-xl">
                    IN
                  </AvatarFallback>
                </Avatar>
              </div>
            </div>

            {/* Main content with two columns */}
            <div className="flex justify-between pt-10 text-white mt-6">
              {/* Left side - Points */}
              <div className="flex flex-col items-center flex-1">
                <span className="text-5xl font-bold mb-1">{pointsCollected}</span>
                <span className="text-sm opacity-80">{t('home.pointsCollected')}</span>
              </div>

              {/* Right side - Hours */}
              <div className="flex flex-col items-center flex-1">
                <span className="text-5xl font-bold mb-1">{hoursLeft}</span>
                <span className="text-sm opacity-80">{t('home.hoursLeft')}</span>
              </div>
            </div>
          </div>

          {/* Bottom tabs */}
          <div className="absolute bottom-0 left-0 right-0 transform translate-y-1/2">
            <div className="mx-auto bg-white rounded-full py-2 px-4 flex justify-between items-center shadow-lg max-w-xs">
              <span className="text-gray-700 font-medium px-2">Camps</span>
              <span className="text-white font-medium bg-teal-500 rounded-full px-3 py-1 text-xs">am</span>
              <span className="text-gray-500 font-medium px-2">Tasks</span>
              <span className="text-gray-500 font-medium px-2">Stats</span>
              <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                <span className="text-gray-600 text-xs">3</span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg text-gray-500">{t('home.thisWeek')}</h2>
          <div className="flex items-center text-sm text-gray-500">
            <CalendarBlank
              className={`w-4 h-4 ${
                language === 'ar' ? 'ml-1' : 'mr-1'
              }`}
            />
            <span>{t('home.dateRange')}</span>
          </div>
        </div>

        {/* Vertical stack of stats cards */}
        <div className="space-y-3 grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* First stat card */}
          <Link to="/plan" className="block">
            <Card className="shadow-md border border-gray-200">
              <CardContent className="p-4 pb-0 relative overflow-hidden">
                {/* Background pattern */}
                <div className="absolute top-0 right-0 w-24 h-24 opacity-5 pointer-events-none">
                  <div className="grid grid-cols-6 gap-2">
                    {Array.from({ length: 36 }).map((_, i) => (
                      <div
                        key={i}
                        className="w-1 h-1 rounded-full bg-[var(--color-gold-500)]"
                      ></div>
                    ))}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="p-2 bg-opacity-10 bg-[var(--color-gold-500)] rounded-lg">
                    <ClipboardText className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-500">
                      {t('home.inspections')}
                    </p>
                    <div className="flex items-baseline gap-2">
                      <h2 className="text-3xl font-normal">1</h2>
                      <span className="text-sm text-gray-500">
                        / 3
                      </span>
                    </div>

                    {/* Trend indicator */}
                    <div className="flex items-center gap-1 text-xs text-emerald-600">
                      <TrendUp className="w-3 h-3" />
                      <span>15% from last period</span>
                    </div>
                  </div>
                </div>

                {/* Mini chart */}
                <div className="absolute bottom-12 right-4 opacity-30 pointer-events-none">
                  <ChartLineUp className="w-8 h-8 text-[var(--color-gold-500)]" />
                </div>

                <div className="mt-4 -mx-4 px-4 py-3 bg-gray-100 text-center sm:text-start">
                  <span className="text-sm text-[var(--color-gold-500)]">
                    {t('common.viewAll')}
                  </span>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Second stat card */}
          <Link to="/hours" className="block">
            <Card className="shadow-md border border-gray-200">
              <CardContent className="p-4 pb-0 relative overflow-hidden">
                {/* Background pattern */}
                <div className="absolute top-0 right-0 w-24 h-24 opacity-5 pointer-events-none">
                  <div className="flex flex-col gap-2">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div
                        key={i}
                        className="w-full h-0.5 bg-[var(--color-gold-500)]"
                      ></div>
                    ))}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="p-2 bg-opacity-10 bg-[var(--color-gold-500)] rounded-lg">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-500">
                      {t('home.hoursSpent')}
                    </p>
                    <div className="flex items-baseline gap-2">
                      <h2 className="text-3xl font-normal">6.5</h2>
                      <span className="text-sm text-gray-500">
                        / 8
                      </span>
                    </div>

                    {/* Trend indicator */}
                    <div className="flex items-center gap-1 text-xs text-red-600">
                      <TrendUp className="w-3 h-3 rotate-180" />
                      <span>5% from last period</span>
                    </div>
                  </div>
                </div>

                {/* Mini chart */}
                <div className="absolute bottom-12 right-4 opacity-30 pointer-events-none">
                  <ChartLineUp className="w-8 h-8 text-[var(--color-gold-500)]" />
                </div>

                <div className="mt-4 -mx-4 px-4 py-3 bg-gray-100 text-center sm:text-start">
                  <span className="text-sm text-[var(--color-gold-500)]">
                    {t('common.viewAll')}
                  </span>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Third stat card */}
          <Link to="/analytics" className="block">
            <Card className="shadow-md border border-gray-200">
              <CardContent className="p-4 pb-0 relative overflow-hidden">
                {/* Background pattern */}
                <div className="absolute top-0 right-0 w-24 h-24 opacity-5 pointer-events-none">
                  <svg
                    viewBox="0 0 100 100"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-full h-full fill-[var(--color-gold-500)]"
                  >
                    <path d="M0,50 C20,35 35,65 55,50 C75,35 80,65 100,50 L100,100 L0,100 Z" />
                  </svg>
                </div>

                <div className="flex items-start gap-3">
                  <div className="p-2 bg-opacity-10 bg-[var(--color-gold-500)] rounded-lg">
                    <ChartLineUp className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-500">
                      {t('home.avgInspectionTime')}
                    </p>
                    <div className="flex items-baseline gap-2">
                      <h2 className="text-3xl font-normal">2.25</h2>
                      <span className="text-sm text-gray-500">
                        {t('common.hours')}
                      </span>
                    </div>

                    {/* Trend indicator */}
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <div className="w-3 h-0.5 bg-gray-500"></div>
                      <span>0% from last period</span>
                    </div>
                  </div>
                </div>

                {/* Mini chart */}
                <div className="absolute bottom-12 right-4 opacity-30 pointer-events-none">
                  <ChartLineUp className="w-8 h-8 text-[var(--color-gold-500)]" />
                </div>

                <div className="mt-4 -mx-4 px-4 py-3 bg-gray-100 text-center sm:text-start">
                  <span className="text-sm text-[var(--color-gold-500)]">
                    {t('common.viewAll')}
                  </span>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-3">
          <Link to="/analytics/monthly" className="block">
            <Card className="shadow-md border border-gray-200">
              <CardContent className="p-4 pb-0">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-2xl font-normal">
                      {monthlyData.current}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {t('home.inspectionsThisMonth')}
                    </p>
                  </div>
                  <div className="flex items-center text-sm text-emerald-600">
                    <TrendUp
                      className={`w-4 h-4 ${
                        language === 'ar' ? 'ml-1' : 'mr-1'
                      }`}
                    />
                    {monthlyData.percentChange}%
                  </div>
                </div>
                <div className="h-[200px] mt-4">
                  <AreaChart
                    data={monthlyData.trend}
                    categories={[
                      {
                        category: 'value',
                        color: 'var(--color-gold-500)', // Using CSS variable
                      },
                    ]}
                    index="date"
                    yAxisWidth={40}
                    tooltipTitle={t('home.inspections')}
                    className="h-[200px]"
                  />
                </div>
                <div className="mt-4 -mx-4 px-4 py-3 bg-gray-100 text-center sm:text-start">
                  <span className="text-sm text-amber-700">
                    {t('common.viewAll')}
                  </span>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/analytics/performance" className="block">
            <Card className="shadow-md border border-gray-200">
              <CardContent className="p-4 pb-0">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-2xl font-normal">
                      {performanceData.efficiency}%
                    </h3>
                    <p className="text-sm text-gray-500">
                      {t('home.inspectionEfficiency')}
                    </p>
                  </div>
                  <div className="flex items-center text-sm text-emerald-600">
                    <TrendUp
                      className={`w-4 h-4 ${
                        language === 'ar' ? 'ml-1' : 'mr-1'
                      }`}
                    />
                    4%
                  </div>
                </div>
                <div className="h-[200px] mt-4">
                  <AreaChart
                    data={performanceData.trend}
                    categories={[
                      {
                        category: 'value',
                        color: 'var(--color-gold-500)', // Using CSS variable
                      },
                    ]}
                    index="date"
                    yAxisWidth={40}
                    tooltipTitle={t('home.inspectionEfficiency')}
                    className="h-[200px]"
                  />
                </div>
                <div className="mt-4 -mx-4 px-4 py-3 bg-gray-100 text-center sm:text-start">
                  <span className="text-sm text-[var(--color-gold-500)]">
                    {t('common.viewAll')}
                  </span>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>
    </div>
  );
}
